import { useEffect } from "react";

/**
 * Sets document title, meta description, canonical, and structured JSON-LD data.
 * Googlebot fully renders JS pages, so runtime injection is picked up on subsequent crawls.
 * This is essential because CRA's HTML minifier strips inline JSON-LD from index.html at build time.
 */
const useSEO = ({ title, description, canonical, jsonLd }) => {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (name, content, isProperty = false) => {
      if (!content) return;
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:url", canonical, true);
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    if (canonical) {
      let link = document.querySelector("link[rel='canonical']");
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    // Inject route-specific JSON-LD structured data.
    // We tag each block so we can replace them cleanly on route changes.
    const injectedIds = [];
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((data, idx) => {
        const id = `route-jsonld-${idx}`;
        injectedIds.push(id);
        // Remove any existing block with same id (from previous route)
        const existing = document.getElementById(id);
        if (existing) existing.remove();
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = id;
        script.text = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }

    return () => {
      // Clean up route-specific JSON-LD on unmount so the next page can inject its own
      injectedIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [title, description, canonical, jsonLd]);
};

export default useSEO;
